import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Drawer,
  List,
  ListItem,
  Typography,
  Box,
  Stack,
  Pagination,
  Container,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Skeleton
} from '@mui/material';
import { 
  ShoppingCart as ShoppingCartIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import { API_BASE_URL, authenticatedFetch } from "../config/api";
import apiTokenService from '../services/apiTokenService';
import gtmService from '../services/gtmService';
import { trackAddToCart } from '../services/metaPixel';
import FastImage from './FastImage';
import "./Store.css";

const SkipLink = () => (
  <a href="#main-content" className="skip-link">
    تخطي إلى المحتوى الرئيسي
  </a>
);

const ErrorBoundary = ({ error, retry }) => (
  <Paper className="error-boundary">
    <Typography variant="h6" color="error" gutterBottom>
      خطأ في تحميل البيانات
    </Typography>
    <Typography variant="body2" color="textSecondary" paragraph>
      {error || "حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى."}
    </Typography>
    <Button variant="outlined" onClick={retry}>
      إعادة المحاولة
    </Button>
  </Paper>
);

const LoadingSkeleton = () => (
  <Grid container spacing={1} className="products-grid-container">
    {[...Array(12)].map((_, index) => (
      <Grid item xs={4} sm={4} md={3} lg={2} key={index}>
        <Card className="product-skeleton">
          <Skeleton variant="rectangular" height={200} />
          <CardContent>
            <Skeleton variant="text" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rectangular" height={32} sx={{ mt: 1 }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

function Store() {
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]); // Add offers state like Home page
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cachedProductOrder, setCachedProductOrder] = useState(() => {
    // Try to restore cached product order from sessionStorage
    try {
      const cached = sessionStorage.getItem('storeProductOrder');
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error("Error loading cached product order:", error);
      return null;
    }
  });
  const [cachedPageState, setCachedPageState] = useState(() => {
    // Try to restore cached page state from sessionStorage
    try {
      const cached = sessionStorage.getItem('storePageState');
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error("Error loading cached page state:", error);
      return null;
    }
  });
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("cart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Error loading cart from localStorage:", error);
      return [];
    }
  });
  const [filter, setFilter] = useState({ brand_id: "", category_id: "", search: "" });
  const [sortBy, setSortBy] = useState(""); // For price sorting
  const [searchQuery, setSearchQuery] = useState(""); // For debounced search
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [cartKey, setCartKey] = useState(0);
  const [isUrlProcessed, setIsUrlProcessed] = useState(false); // Track if URL has been processed

  const navigate = useNavigate();
  const location = useLocation();
  const lastLocationSearch = useRef(location.search);

  // Generate a cache key based on current filters to maintain product order
  const getCacheKey = () => {
    const searchParams = new URLSearchParams(location.search);
    const category = searchParams.get('category') || searchParams.get('category_id') || '';
    const brand = searchParams.get('brand') || searchParams.get('brand_id') || '';
    const search = searchParams.get('search') || '';
    return `${category}-${brand}-${search}-${sortBy}`;
  };

  const showMessage = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== filter.search) {
        handleSearch(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only load data if URL has been processed to avoid premature API calls
    if (isUrlProcessed) {
      loadData();
    }
  }, [filter, page, isUrlProcessed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadFiltersData();
    // Cart is now initialized directly from localStorage in useState
  }, []);

  // Load offers from API (products with discount_price > 0) - same as Home page
  useEffect(() => {
    const loadOffers = async () => {
      try {
        const response = await apiTokenService.fetch(`${API_BASE_URL}/api/offers.php?limit=100`);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.products && Array.isArray(data.products)) {
            setOffers(data.products);
          } else {
            setOffers([]);
          }
        } else {
          setOffers([]);
        }
      } catch (error) {
        console.error('Store: Error loading offers:', error);
        setOffers([]);
      }
    };

    loadOffers();
  }, []);

  // -- THIS IS THE ONLY PLACE THAT CHANGES FILTER STATE --
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category') || searchParams.get('category_id');
    const brandParam = searchParams.get('brand') || searchParams.get('brand_id'); // Also check for brand_id
    const searchParam = searchParams.get('search');
    
    let validCategoryId = "";
    if (categoryParam) {
      if (categories.length > 0) {
        const categoryExists = categories.some(cat => String(cat.id) === String(categoryParam));
        if (categoryExists) {
          validCategoryId = String(categoryParam);
        }
      } else {
        validCategoryId = String(categoryParam);
      }
    }
    let validBrandId = "";
    if (brandParam) {
      validBrandId = String(brandParam);
    }
    const newFilter = {
      category_id: validCategoryId,
      brand_id: validBrandId,
      search: searchParam || ""
    };
    
    setFilter(newFilter);
    setSearchQuery(searchParam || "");
    
    // Try to restore cached page state for this filter combination
    const currentCacheKey = `${validCategoryId}-${validBrandId}-${searchParam || ''}-${sortBy}`;
    if (cachedPageState && cachedPageState.cacheKey === currentCacheKey) {
      // Restore the cached page
      setPage(cachedPageState.page || 1);
    } else {
      // No cache for this filter combination, start from page 1
      setPage(1);
    }
    
    setIsUrlProcessed(true); // Mark URL as processed so API calls can proceed
  }, [location.search, categories, sortBy, cachedPageState]); // Added cachedPageState and sortBy as dependencies

  // Validate category filter when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && filter.category_id) {
      const categoryExists = categories.some(cat => String(cat.id) === String(filter.category_id));
      if (!categoryExists) {
        setFilter(prev => ({ ...prev, category_id: "" }));
      }
    }
  }, [categories, filter.category_id]);

  // Clear cache when filters or sortBy change (user wants fresh results)
  useEffect(() => {
    const currentCacheKey = getCacheKey();
    if (cachedProductOrder && cachedProductOrder.cacheKey !== currentCacheKey) {
      // Filters changed, clear the cache to get fresh random order
      setCachedProductOrder(null);
      try {
        sessionStorage.removeItem('storeProductOrder');
      } catch (error) {
        console.error('Error clearing product order cache:', error);
      }
    }
    
    // Also clear page state cache when filters change
    if (cachedPageState && cachedPageState.cacheKey !== currentCacheKey) {
      setCachedPageState(null);
      try {
        sessionStorage.removeItem('storePageState');
      } catch (error) {
        console.error('Error clearing page state cache:', error);
      }
    }
  }, [filter.category_id, filter.brand_id, filter.search, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save page state when page changes (but not when filters change)
  useEffect(() => {
    if (isUrlProcessed && page > 0) {
      const currentCacheKey = getCacheKey();
      const pageStateData = {
        cacheKey: currentCacheKey,
        page: page,
        timestamp: Date.now()
      };
      
      setCachedPageState(pageStateData);
      
      try {
        sessionStorage.setItem('storePageState', JSON.stringify(pageStateData));
      } catch (error) {
        console.error('Error saving page state to cache:', error);
      }
    }
  }, [page, isUrlProcessed]); // Only depend on page and isUrlProcessed

  useEffect(() => {
    if (cart.length >= 0) {
      localStorage.setItem("cart", JSON.stringify(cart));
      setCartKey(prev => prev + 1);
      window.dispatchEvent(new CustomEvent('cartUpdated', { 
        detail: { cartItems: cart, cartLength: cart.length }
      }));
    }
  }, [cart]);

  const loadFiltersData = async () => {
    try {
      setError(null);
      const brandsResponse = await fetch(`${API_BASE_URL}/api/brands.php`);
      if (brandsResponse.ok) {
        const brandsData = await brandsResponse.json();
        if (Array.isArray(brandsData)) {
          setBrands(brandsData);
        } else if (brandsData.brands && Array.isArray(brandsData.brands)) {
          setBrands(brandsData.brands);
        } else if (brandsData.data && Array.isArray(brandsData.data)) {
          setBrands(brandsData.data);
        } else {
          const productsResponse = await apiTokenService.fetch(`${API_BASE_URL}/api/products.php?all=true`);
          const productsData = await productsResponse.json();
          if (productsData && productsData.products && Array.isArray(productsData.products)) {
            const brandsMap = new Map();
            productsData.products.forEach(product => {
              if (product.brand_id && product.brand_name) {
                brandsMap.set(product.brand_id, {
                  id: product.brand_id,
                  name: product.brand_name.trim()
                });
              } else if (product.brand && product.brand.trim()) {
                brandsMap.set(product.brand, {
                  id: product.brand,
                  name: product.brand.trim()
                });
              }
            });
            const brandsArray = Array.from(brandsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
            setBrands(brandsArray);
          }
        }
      }
      const categoriesResponse = await fetch(`${API_BASE_URL}/api/categories.php`);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
      }
    } catch (error) {
      setBrands([]);
      setCategories([]);
      setError("خطأ في تحميل البيانات المساعدة");
      showMessage("خطأ في تحميل البيانات المساعدة", "warning");
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // First, fetch products with offers (discount_price > 0) from offers API
      let offerProducts = [];
      try {
        const offersResponse = await apiTokenService.fetch(`${API_BASE_URL}/api/offers.php?limit=100`);
        if (offersResponse.ok) {
          const offersData = await offersResponse.json();
          if (offersData.success && offersData.products && Array.isArray(offersData.products)) {
            offerProducts = offersData.products;
          }
        }
      } catch (offerError) {
        console.error('Could not fetch offers, continuing with regular products:', offerError);
      }

      // Then fetch regular products - get all products for proper caching
      const params = new URLSearchParams({
        per_page: 'all', // Fetch all products to enable proper pagination caching
        ...(filter.category_id && { category_id: filter.category_id }),
        ...(filter.search && { search: filter.search }),
        ...(filter.brand_id && { brand: filter.brand_id })
      });
      const apiUrl = `${API_BASE_URL}/api/products.php?${params}`;
      const response = await apiTokenService.fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      let regularProducts = data.products || [];

      // Apply filters to offer products if any filters are active
      let filteredOfferProducts = offerProducts;
      if (filter.category_id || filter.search || filter.brand_id) {
        filteredOfferProducts = offerProducts.filter(product => {
          // Category filter
          if (filter.category_id && String(product.category_id) !== String(filter.category_id)) {
            return false;
          }
          // Search filter
          if (filter.search && product.name && !product.name.toLowerCase().includes(filter.search.toLowerCase())) {
            return false;
          }
          // Brand filter
          if (filter.brand_id && String(product.brand_id) !== String(filter.brand_id)) {
            return false;
          }
          return true;
        });
      }

      // Don't remove offer products - let them appear in both sections
      // Users should see offer products in both the offers strip AND regular grid

      // Combine: offers first, then regular products (may include duplicates, but that's intentional)
      const combinedProducts = [...filteredOfferProducts, ...regularProducts];
      
      // Check if we have a cached order for this filter combination
      const currentCacheKey = getCacheKey();
      let finalProducts = combinedProducts;
      
      if (cachedProductOrder && cachedProductOrder.cacheKey === currentCacheKey) {
        // Restore the cached product order
        const cachedIds = cachedProductOrder.productIds;
        const productMap = new Map(combinedProducts.map(p => [p.id, p]));
        
        // Reconstruct products in cached order, filtering out any that no longer exist
        finalProducts = cachedIds
          .map(id => productMap.get(id))
          .filter(Boolean); // Remove any undefined products
        
        // Add any new products that weren't in the cache to the end
        const existingIds = new Set(cachedIds);
        const newProducts = combinedProducts.filter(p => !existingIds.has(p.id));
        finalProducts = [...finalProducts, ...newProducts];
      } else {
        // No cache or different filters - create new order and cache it
        // Apply initial randomization for fresh browsing experience
        finalProducts = combinedProducts.sort(() => Math.random() - 0.5);
        
        // Cache the product order for this filter combination
        const newCacheData = {
          cacheKey: currentCacheKey,
          productIds: finalProducts.map(p => p.id),
          timestamp: Date.now()
        };
        setCachedProductOrder(newCacheData);
        
        try {
          sessionStorage.setItem('storeProductOrder', JSON.stringify(newCacheData));
        } catch (error) {
          console.error('Error saving product order to cache:', error);
        }
      }
      
      // Store all products and set total count
      setProducts(finalProducts);
      setTotal(finalProducts.length);
    } catch (error) {
      console.error('Error in loadData:', error);
      let errorMessage = "خطأ في الاتصال بالخادم";
      if (error.message.includes('HTTP 404')) {
        errorMessage = "لم يتم العثور على ملف المنتجات في الخادم";
      } else if (error.message.includes('HTTP 500')) {
        errorMessage = "خطأ في الخادم، يرجى المحاولة لاحقاً";
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = "فشل في الاتصال بالخادم، تحقق من الاتصال بالإنترنت";
      } else if (error.message.includes('CORS')) {
        errorMessage = "خطأ في إعدادات الخادم (CORS)";
      }
      setError(errorMessage);
      showMessage(errorMessage, "error");
      setProducts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = (product) => {
    const quantity = 1;
    const existingItemIndex = cart.findIndex(item => item.id === product.id);
    let newCart;
    if (existingItemIndex > -1) {
      newCart = cart.map((item, index) =>
        index === existingItemIndex
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      newCart = [...cart, { ...product, quantity }];
    }
    setCart(newCart);
    showMessage(`تم إضافة ${product.name} إلى السلة`);
    
    // Track add to cart event with GTM
    gtmService.trackAddToCart({
      currency: 'SAR',
      value: parseFloat(product.price) || 0,
      items: [{
        item_id: product.id,
        item_name: product.name,
        category: product.category || 'General',
        quantity: quantity,
        price: parseFloat(product.price) || 0
      }]
    });
    
    // Track add to cart event with Meta Pixel
    const trackingPrice = parseFloat(product.after_discount && product.after_discount < product.sell_price 
      ? product.after_discount 
      : product.sell_price || product.price) || 0;
    
    trackAddToCart({
      id: String(product.id),
      name: product.name,
      price: trackingPrice
    });
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    const newCart = cart.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    setCart(newCart);
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.id !== productId);
    setCart(newCart);
    showMessage("تم حذف المنتج من السلة");
  };

  const clearCart = () => {
    setCart([]);
    showMessage("تم تفريغ السلة");
  };

  const getPrice = (product) => {
    if (!product) return 0;
    if (product.type === 'gift' && product.points_cost) {
      return Number(product.points_cost) || 0;
    }
    if (product.after_discount && Number(product.after_discount) > 0) {
      return Number(product.after_discount);
    }
    if (product.discount_price && Number(product.discount_price) > 0) {
      return Number(product.discount_price);
    }
    if (product.price && Number(product.price) > 0) {
      return Number(product.price);
    }
    return Number(product.sell_price || 0);
  };

  const getTotalPrice = () => {
    try {
      return cart.reduce((total, item) => {
        if (!item || item.type === 'gift') return total;
        const price = getPrice(item) || 0;
        const quantity = item.quantity || 0;
        return total + (price * quantity);
      }, 0) || 0;
    } catch (error) {
      console.error('Error calculating total price:', error);
      return 0;
    }
  };

  const getTotalPoints = () => {
    try {
      return cart.reduce((total, item) => {
        if (!item || item.type !== 'gift') return total;
        const price = getPrice(item) || 0;
        const quantity = item.quantity || 0;
        return total + (price * quantity);
      }, 0) || 0;
    } catch (error) {
      console.error('Error calculating total points:', error);
      return 0;
    }
  };

  const getTotalItems = () => {
    try {
      return cart.reduce((total, item) => {
        if (!item) return total;
        return total + (item.quantity || 0);
      }, 0) || 0;
    } catch (error) {
      console.error('Error calculating total items:', error);
      return 0;
    }
  };

  const getCombinedTotal = () => {
    try {
      return cart.reduce((total, item) => {
        if (!item) return total;
        const price = getPrice(item) || 0;
        const quantity = item.quantity || 0;
        return total + (price * quantity);
      }, 0) || 0;
    } catch (error) {
      console.error('Error calculating combined total:', error);
      return 0;
    }
  };

  const cartStats = {
    totalItems: getTotalItems() || 0,
    totalPrice: getTotalPrice() || 0,
    totalPoints: getTotalPoints() || 0,
    combinedTotal: getCombinedTotal() || 0,
    itemCount: (cart && cart.length) || 0,
    hasRegularItems: cart && cart.some(item => item && item.type !== 'gift'),
    hasGiftItems: cart && cart.some(item => item && item.type === 'gift')
  };

  // Listen for cart changes from other components (like Header clearing cart)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'cart') {
        try {
          const newCart = e.newValue ? JSON.parse(e.newValue) : [];
          setCart(newCart);
        } catch (error) {
          console.error("Error parsing cart data from storage:", error);
          setCart([]);
        }
      }
    };

    const handleCartUpdate = (e) => {
      if (e.detail && e.detail.cartItems) {
        setCart(e.detail.cartItems);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  const handleSearch = (value) => {
    setPage(1);
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set('search', value);
      // Track search event with GTM
      gtmService.trackSearch(value);
    } else {
      searchParams.delete('search');
    }
    navigate(`/store?${searchParams.toString()}`, { replace: true });
  };

  const handleBrandChange = (brandId) => {
    setPage(1);
    const searchParams = new URLSearchParams(location.search);
    if (brandId) {
      searchParams.set('brand', brandId);
    } else {
      searchParams.delete('brand');
    }
    navigate(`/store?${searchParams.toString()}`, { replace: true });
  };

  const handleCategoryChange = (categoryId) => {
    setPage(1);
    const searchParams = new URLSearchParams(location.search);
    if (categoryId) {
      searchParams.set('category', categoryId);
      searchParams.delete('category_id');
    } else {
      searchParams.delete('category');
      searchParams.delete('category_id');
    }
    navigate(`/store?${searchParams.toString()}`, { replace: true });
  };

  const handlePageChange = (event, newPage) => {
    // Update page state
    setPage(newPage);
    
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuantityChange = (productId, quantity) => {
    setQuantities(prev => ({ ...prev, [productId]: quantity }));
  };

  const renderActiveFilters = () => {
    const activeFilters = [];
    if (filter.search) {
      activeFilters.push({
        label: `البحث: ${filter.search}`,
        key: 'search',
        onRemove: () => {
          setPage(1);
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete('search');
          navigate(`/store?${searchParams.toString()}`, { replace: true });
        }
      });
    }
    if (filter.brand_id) {
      const brand = brands.find(b => String(b.id) === String(filter.brand_id));
      if (brand) {
        activeFilters.push({
          label: `الماركة: ${brand.name}`,
          key: 'brand',
          onRemove: () => {
            setPage(1);
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('brand');
            navigate(`/store?${searchParams.toString()}`, { replace: true });
          }
        });
      }
    }
    if (filter.category_id) {
      const category = categories.find(c => String(c.id) === String(filter.category_id));
      if (category) {
        activeFilters.push({
          label: `التصنيف: ${category.name}`,
          key: 'category',
          onRemove: () => {
            setPage(1);
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('category');
            searchParams.delete('category_id');
            navigate(`/store?${searchParams.toString()}`, { replace: true });
          }
        });
      }
    }
    if (activeFilters.length === 0) return null;
    return (
      <Box className="filter-chips-container">
        {activeFilters.map(filter => (
          <Chip
            key={filter.key}
            label={filter.label}
            onDelete={filter.onRemove}
            className="filter-chip"
            size="small"
          />
        ))}
      </Box>
    );
  };

  const renderProductCard = (product) => {
    // Use discount_price > 0 to determine if product has an offer (like Home page)
    const originalPrice = Number(product.sell_price || 0);
    const discountAmount = Number(product.discount_price || 0);
    const finalPrice = Number(product.after_discount || 0);
    const hasOffer = discountAmount > 0; // Main condition for showing enhanced styling
    const discountPercentage = hasOffer && originalPrice > 0 ? Math.round((discountAmount / originalPrice) * 100) : 0;
    const savings = discountAmount; // discount_price is the savings amount

    return (
      <Grid item xs={4} sm={4} md={3} lg={2} key={product.id}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: hasOffer ? 'white' : 'transparent',
            boxShadow: hasOffer ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
            border: hasOffer ? '1px solid #f0f0f0' : 'none',
            borderRadius: hasOffer ? 2 : 0,
            p: hasOffer ? 1 : 0,
            position: 'relative',
            cursor: 'pointer',
            '&:hover': {
              transform: hasOffer ? 'translateY(-6px) scale(1.02)' : 'translateY(-4px)',
              boxShadow: hasOffer ? '0 8px 20px rgba(0, 0, 0, 0.15)' : 'none',
              transition: 'all 0.3s ease'
            }
          }}
          className="product-card-optimized"
          onClick={() => {
            // Track product view
            gtmService.trackProductView({
              currency: 'SAR',
              value: parseFloat(product.price) || 0,
              items: [{
                item_id: product.id,
                item_name: product.name,
                category: product.category || 'General',
                price: parseFloat(product.price) || 0
              }]
            });
            navigate(`/product/${product.id}`);
          }}
        >
          {product.stock !== undefined && (
            <Box
              className={`stock-indicator ${
                product.stock > 10 ? 'in-stock' : 
                product.stock > 0 ? 'low-stock' : 'out-of-stock'
              }`}
            >
              {product.stock > 10 ? 'متوفر' : 
               product.stock > 0 ? `${product.stock} متبقي` : 'نفد المخزون'}
            </Box>
          )}

          {/* Enhanced Discount Badge for offers (discount_price > 0) */}
          {hasOffer && discountPercentage > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                background: '#ff4444',
                color: 'white',
                borderRadius: '50%',
                width: 35,
                height: 35,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                zIndex: 2,
                boxShadow: '0 2px 8px rgba(255, 68, 68, 0.3)'
              }}
            >
              -{discountPercentage}%
            </Box>
          )}

          <FastImage
            src={product.image && typeof product.image === 'string' && product.image.trim() ? 
              (product.image.startsWith('http') ? 
                product.image : 
                `https://al-sadik.com/api/uploads/products/${product.image.replace('uploads/', '')}`
              ) : '/placeholder-image.jpg'
            }
            alt={product.name || 'Product image'}
            aspectRatio="1"
            priority={false} // All images lazy load in store
            sx={{
              width: '100%',
              borderRadius: hasOffer ? 1 : 0,
              mb: 1
            }}
          />
          <Box sx={{ width: '100%', textAlign: 'center', px: 1, pb: hasOffer ? 0 : 1 }}>
            <Typography variant="caption" sx={{ color: 'grey.600', fontSize: '0.85rem', mb: 0.5, display: 'block', fontWeight: 400 }}>
              {product.brand_id
                ? brands.find(b => String(b.id) === String(product.brand_id))?.name || product.brand || ''
                : product.brand || ''}
            </Typography>
            <Typography variant="h6" component="h3" sx={{
              fontWeight: 'bold',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              mb: 0.5,
              height: '2.2em',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.1,
              wordBreak: 'break-word',
              color: hasOffer ? '#333' : 'grey.700'
            }}>
              {product.name}
            </Typography>

            {/* Enhanced pricing for offers (discount_price > 0) */}
            {hasOffer ? (
              <Box sx={{ mb: 1 }}>
                {/* Original price (crossed out) */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.7rem',
                    color: '#999',
                    textDecoration: 'line-through',
                    mb: 0.2
                  }}
                >
                  {originalPrice.toLocaleString()} د.ع
                </Typography>
                
                {/* Final price after discount */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    color: '#ff4444',
                    mb: 0.2
                  }}
                >
                  {finalPrice.toLocaleString()} د.ع
                </Typography>
                
                {/* Savings amount */}
                {savings > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.65rem',
                      color: '#ff4444',
                      fontWeight: 'bold'
                    }}
                  >
                    وفر {savings.toLocaleString()} د.ع
                  </Typography>
                )}
              </Box>
            ) : (
              /* Regular pricing */
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  color: 'text.primary',
                  mb: 1,
                  height: '1.4em',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}
              >
                {(getPrice(product) || 0).toLocaleString()} د.ع
              </Typography>
            )}

            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (e.currentTarget && e.currentTarget.classList) {
                  e.currentTarget.classList.add('clicked');
                  setTimeout(() => {
                    if (e.currentTarget && e.currentTarget.classList) {
                      e.currentTarget.classList.remove('clicked');
                    }
                  }, 600);
                }
                addToCart(product);
              }}
              disabled={product.stock === 0}
              className="add-to-cart-feedback"
              sx={{
                mt: 1,
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                py: 0.75,
                px: 2,
                display: 'block',
                margin: '8px auto 0 auto',
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: '#3b5dac',
                color: '#fff',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  transform: 'translateY(-1px)',
                  backgroundColor: '#2a406e',
                  color: '#fff',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {product.stock === 0 ? 'نفد المخزون' : 'أضف للسلة'}
            </Button>
          </Box>
        </Box>
      </Grid>
    );
  };

  const CartDrawer = () => (
    <Drawer
      anchor="right"
      open={isCartOpen}
      onClose={() => setIsCartOpen(false)}
      key={`cart-drawer-${cartKey}`}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          maxWidth: '400px'
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            سلة التسوق ({getTotalItems()})
          </Typography>
          <Button onClick={() => setIsCartOpen(false)}>
            إغلاق
          </Button>
        </Box>
        {!cart || cart.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }} className="cart-empty-container">
            <ShoppingCartIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
            <Typography color="text.secondary">
              السلة فارغة
            </Typography>
          </Box>
        ) : (
          <>
            <List key={`cart-list-${cartKey}`} sx={{ maxHeight: 400, overflow: 'auto' }} className="cart-items-list">
              {cart && cart.filter(item => item && item.id).map((item, index) => {
                const price = getPrice(item);
                const displayPrice = typeof price === "number" && !isNaN(price) ? price : 0;
                return (
                <ListItem key={`cart-item-${item.id}-${item.quantity || 0}-${cartKey}`} divider className="cart-item-enter">
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {item.name || 'Unknown Item'}
                        </Typography>
                        {item.type === 'gift' && (
                          <Chip 
                            label="هدية" 
                            size="small" 
                            color="secondary" 
                            sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => removeFromCart(item.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {(displayPrice || 0).toLocaleString()} {item.type === 'gift' ? 'نقطة للقطعة' : 'د.ع للقطعة'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                          size="small"
                          onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography sx={{ mx: 2 }}>
                          {item.quantity}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {(displayPrice * (item.quantity || 1)).toLocaleString()} {item.type === 'gift' ? 'نقطة' : 'د.ع'}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
                );
              })}
            </List>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }} className="cart-total-container">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">الإجمالي:</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }} className="cart-total-text">
                    {(cartStats.combinedTotal || 0).toLocaleString()} {cartStats.hasGiftItems && cartStats.hasRegularItems ? 'وحدة' : cartStats.hasGiftItems ? 'نقطة' : 'د.ع'}
                  </Typography>
                  {cartStats.hasGiftItems && cartStats.hasRegularItems && (
                    <Box sx={{ mt: 1, fontSize: '0.8rem', color: 'text.secondary' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {(cartStats.totalPrice || 0).toLocaleString()} د.ع + {(cartStats.totalPoints || 0).toLocaleString()} نقطة
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Stack spacing={1}>
                <Button 
                  variant="contained" 
                  fullWidth 
                  onClick={() => {
                    setIsCartOpen(false);
                    navigate('/checkout');
                  }}
                  disabled={cart.length === 0}
                  className="checkout-btn"
                >
                  إتمام الطلب
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={clearCart}
                  disabled={cart.length === 0}
                  className="empty-cart-btn"
                >
                  تفريغ السلة
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );

  const retryLoad = () => {
    setError(null);
    loadData();
    loadFiltersData();
  };

  // Generate JSON-LD structured data for the store page
  const generateStoreJsonLd = () => {
    return {
      "@context": "https://schema.org",
      "@type": "Store",
      "name": "Al Sadik Store",
      "description": "متجر الصادق للمنتجات المتنوعة",
      "url": window.location.href,
      "currenciesAccepted": "IQD",
      "paymentAccepted": "Cash",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Al Sadik Product Catalog",
        "itemListElement": products.slice(0, 20).map(product => {
          const price = product.sell_price || product.after_discount || 0;
          const hasValidPrice = price && price > 0;
          
          return {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": product.name || "منتج",
              "description": product.description || product.name || "منتج من متجر الصادق",
              "productID": product.id.toString(),
              "image": product.image ? (
                product.image.startsWith('http') ? 
                  product.image : 
                  product.image.startsWith('api/uploads/products/') ? 
                    `${API_BASE_URL}/${product.image}` : 
                    `https://al-sadik.com/api/uploads/products/${product.image}`
              ) : '',
              "brand": {
                "@type": "Brand",
                "name": product.brand || "الصادق"
              },
              "category": product.category_name || "General"
            },
            "price": hasValidPrice ? price.toString() : "0",
            "priceCurrency": "IQD",
            "availability": product.quantity > 0 
              ? "https://schema.org/InStock" 
              : "https://schema.org/OutOfStock",
            "itemCondition": "https://schema.org/NewCondition",
            "seller": {
              "@type": "Organization",
              "name": "متجر الصادق"
            }
          };
        })
      }
    };
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 0, sm: 2, md: 3 } }}>
      {/* JSON-LD Structured Data for Store */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateStoreJsonLd())
        }}
      />
      
      <SkipLink />
      <Paper sx={{ p: 3, mb: 3 }} className="filters-container">
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
          متجر الصادق
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>الماركة</InputLabel>
              <Select
                value={filter.brand_id && brands.some(brand => String(brand.id) === String(filter.brand_id)) ? filter.brand_id : ""}
                label="الماركة"
                onChange={(e) => handleBrandChange(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  }
                }}
              >
                <MenuItem value="">جميع الماركات</MenuItem>
                {brands.map(brand => (
                  <MenuItem 
                    key={brand.id} 
                    value={brand.id.toString()}
                    sx={{
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      padding: '8px 16px',
                      minHeight: 'auto',
                      lineHeight: 1.2
                    }}
                  >
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>التصنيف</InputLabel>
              <Select
                value={filter.category_id && categories.some(category => String(category.id) === String(filter.category_id)) ? filter.category_id : ""}
                label="التصنيف"
                onChange={(e) => handleCategoryChange(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  }
                }}
              >
                <MenuItem value="">جميع التصنيفات</MenuItem>
                {categories.map(category => (
                  <MenuItem 
                    key={category.id} 
                    value={category.id.toString()}
                    sx={{
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      padding: '8px 16px',
                      minHeight: 'auto',
                      lineHeight: 1.2
                    }}
                  >
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>ترتيب حسب السعر</InputLabel>
              <Select
                value={sortBy}
                label="ترتيب حسب السعر"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="">بدون ترتيب</MenuItem>
                <MenuItem value="price_low_high">السعر: من الأقل للأعلى</MenuItem>
                <MenuItem value="price_high_low">السعر: من الأعلى للأقل</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {renderActiveFilters()}
      </Paper>
      <main id="main-content">
        {error && (
          <ErrorBoundary error={error} retry={retryLoad} />
        )}
        {isLoading ? (
          <LoadingSkeleton />
        ) : products.length === 0 && !error ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ShoppingBagIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              لا توجد منتجات متوفرة
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filter.search || filter.brand_id || filter.category_id 
                ? "جرب تعديل البحث أو المرشحات" 
                : "لم يتم العثور على منتجات في المتجر حالياً"}
            </Typography>
            {(filter.search || filter.brand_id || filter.category_id) && (
              <Button 
                variant="outlined" 
                onClick={() => {
                  setPage(1);
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.delete('search');
                  searchParams.delete('brand');
                  searchParams.delete('category');
                  searchParams.delete('category_id');
                  navigate(`/store?${searchParams.toString()}`, { replace: true });
                }}
              >
                مسح جميع المرشحات
              </Button>
            )}
          </Paper>
        ) : !error && (
          <>
            {/* Discount Products Section - Using Offers API */}
            {(() => {
              // Hide offers section when any filter is active (search, brand, or category)
              const hasActiveFilters = filter.search || filter.brand_id || filter.category_id;
              
              // Use offers state from API instead of filtering products
              if (offers.length > 0 && !hasActiveFilters) {
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: '#ff4444',
                        mb: 2,
                        textAlign: 'center'
                      }}
                    >
                      🔥 العروض المميزة
                    </Typography>
                    {/* Horizontal strip layout for offers */}
                    <Box 
                      sx={{ 
                        display: 'flex',
                        overflowX: 'auto',
                        gap: 2,
                        pb: 1,
                        '&::-webkit-scrollbar': {
                          height: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: '#f1f1f1',
                          borderRadius: 3,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#ccc',
                          borderRadius: 3,
                          '&:hover': {
                            backgroundColor: '#bbb',
                          },
                        },
                      }}
                    >
                      {offers
                        .slice(0, 12) // Show more offers in strip format
                        .sort((a, b) => {
                          // Sort by discount amount (highest first), then randomize within same discount levels
                          const aDiscountAmount = parseFloat(a.discount_price || 0);
                          const bDiscountAmount = parseFloat(b.discount_price || 0);
                          
                          // If discount amounts are significantly different, sort by discount
                          const discountDiff = bDiscountAmount - aDiscountAmount;
                          if (Math.abs(discountDiff) > 1000) { // 1000 IQD threshold for grouping
                            return discountDiff;
                          }
                          
                          // Within similar discount ranges, maintain consistent order
                          // Use product ID as a stable secondary sort to avoid randomness
                          return String(a.id).localeCompare(String(b.id));
                        })
                        .map(product => {
                          // Render compact square cards for strip layout
                          const originalPrice = Number(product.sell_price || 0);
                          const discountAmount = Number(product.discount_price || 0);
                          const finalPrice = Number(product.after_discount || 0);
                          const hasOffer = discountAmount > 0;
                          const discountPercentage = hasOffer && originalPrice > 0 ? Math.round((discountAmount / originalPrice) * 100) : 0;

                          return (
                            <Box
                              key={product.id}
                              sx={{
                                minWidth: 140,
                                maxWidth: 140,
                                height: 200,
                                display: 'flex',
                                flexDirection: 'column',
                                background: 'white',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                border: '1px solid #f0f0f0',
                                borderRadius: 2,
                                p: 1,
                                position: 'relative',
                                cursor: 'pointer',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
                                  transition: 'all 0.3s ease'
                                }
                              }}
                              onClick={() => {
                                // Track product view
                                gtmService.trackProductView({
                                  currency: 'SAR',
                                  value: parseFloat(product.price) || 0,
                                  items: [{
                                    item_id: product.id,
                                    item_name: product.name,
                                    category: product.category || 'General',
                                    price: parseFloat(product.price) || 0
                                  }]
                                });
                                navigate(`/product/${product.id}`);
                              }}
                            >
                              {/* Discount Badge */}
                              {hasOffer && discountPercentage > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    left: 4,
                                    background: '#ff4444',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 28,
                                    height: 28,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.6rem',
                                    fontWeight: 'bold',
                                    zIndex: 2,
                                    boxShadow: '0 2px 4px rgba(255, 68, 68, 0.3)'
                                  }}
                                >
                                  -{discountPercentage}%
                                </Box>
                              )}

                              {/* Square Image */}
                              <FastImage
                                src={product.image && typeof product.image === 'string' && product.image.trim() ? 
                                  (product.image.startsWith('http') ? 
                                    product.image : 
                                    `https://al-sadik.com/api/uploads/products/${product.image.replace('uploads/', '')}`
                                  ) : '/placeholder-image.jpg'
                                }
                                alt={product.name || 'Product image'}
                                aspectRatio="1"
                                priority={false}
                                sx={{
                                  width: '100%',
                                  height: 100,
                                  borderRadius: 1,
                                  mb: 1,
                                  objectFit: 'cover'
                                }}
                              />

                              {/* Product Details */}
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                                {/* Product Name */}
                                <Typography 
                                  variant="body2" 
                                  sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem',
                                    mb: 0.5,
                                    height: '2em',
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1,
                                    color: '#333'
                                  }}
                                >
                                  {product.name}
                                </Typography>

                                {/* Price */}
                                <Box sx={{ mt: 'auto' }}>
                                  {hasOffer ? (
                                    <>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontSize: '0.6rem',
                                          color: '#999',
                                          textDecoration: 'line-through',
                                          display: 'block'
                                        }}
                                      >
                                        {originalPrice.toLocaleString()} د.ع
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 'bold',
                                          fontSize: '0.8rem',
                                          color: '#ff4444'
                                        }}
                                      >
                                        {finalPrice.toLocaleString()} د.ع
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 'bold',
                                        fontSize: '0.8rem',
                                        color: 'text.primary'
                                      }}
                                    >
                                      {(getPrice(product) || 0).toLocaleString()} د.ع
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })
                      }
                    </Box>
                  </Box>
                );
              }
              return null;
            })()}

            {/* Regular Products Section */}
            <Grid container spacing={1} className="products-grid-container">
              {products
                .filter(product => product.active === 1 || product.active === true || product.status === 'active')
                .sort((a, b) => {
                  // If price sorting is selected, sort purely by price
                  if (sortBy === "price_low_high") {
                    const aPrice = parseFloat(a.after_discount && a.after_discount < a.sell_price ? a.after_discount : a.sell_price);
                    const bPrice = parseFloat(b.after_discount && b.after_discount < b.sell_price ? b.after_discount : b.sell_price);
                    return aPrice - bPrice;
                  } else if (sortBy === "price_high_low") {
                    const aPrice = parseFloat(a.after_discount && a.after_discount < a.sell_price ? a.after_discount : a.sell_price);
                    const bPrice = parseFloat(b.after_discount && b.after_discount < b.sell_price ? b.after_discount : b.sell_price);
                    return bPrice - aPrice;
                  }
                  
                  // Default: Maintain cached order (no random sorting)
                  // Products are already in the correct order from loadData function
                  return 0;
                })
                // Client-side pagination: show only products for current page
                .slice((page - 1) * 12, page * 12)
                .map(renderProductCard)}
            </Grid>
            {total > 12 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={Math.ceil(total / 12)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </main>
      <CartDrawer />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Store;